import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ItemDetail, ItemUpdate, SaleUpdate, WriteOff } from "@sable/api-contract";
import { AUDIENCES, CONDITION_GRADES, SEASONS, WRITE_OFF_REASONS, formatWeightParts } from "@sable/domain";
import { IconBack, IconBox, IconDownload, IconLock, IconMore, IconSparkle, IconPencil, IconPlus, IconPrinter, IconStar, IconTrash, IconUnlock } from "../../components/icons.tsx";
import { LabelDialog } from "../../components/LabelDialog.tsx";
import { MeasurementsDialog, measurementSummary } from "../../components/MeasurementsDialog.tsx";
import { PhotoCarousel } from "../../components/PhotoCarousel.tsx";
import { PackagingDialog, packagingSummary } from "../../components/PackagingDialog.tsx";
import { Dialog, ErrorBlock, Field, Loading, MoneyInput, StatusPill } from "../../components/ui.tsx";
import { t, tk } from "../../i18n/index.ts";
import { request } from "../../lib/api.ts";
import { money, shortDate } from "../../lib/format.ts";
import { convertPhoto, photoFileName, saveBlob, zipFiles, type PhotoFormat } from "../../lib/download.ts";
import { preparePhoto } from "../../lib/images.ts";
import { keys, requestEnrich, uploadPhoto, useItem, useItemMutation } from "../../lib/queries.ts";
import { useCan, useWs, wsPath } from "../../lib/workspace.tsx";
import { MarketplacesPanel, TakedownBanner } from "./CrosslistPanel.tsx";
import { PostingPreview } from "./PostingPreview.tsx";
import { IN_STOCK, MoneyPanel, SaleDialog, ShipPanel, activeSale, useMarketplaceName } from "./panels.tsx";

// ---- Photos ------------------------------------------------------------------------------

type ItemPhoto = ItemDetail["photos"][number];

function AddTile({ label, busy, onFiles }: { label: string; busy: boolean; onFiles: (files: FileList) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <button className="add-photo" disabled={busy} onClick={() => input.current?.click()} aria-label={label} title={label}>
      {busy ? <div className="spinner" /> : <IconPlus />}
      <input ref={input} type="file" accept="image/*" multiple onChange={(e) => {
        if (e.target.files?.length) onFiles(e.target.files);
        e.target.value = "";
      }} />
    </button>
  );
}

/** Saves the photo on show, or every photo of the item in one zip, as JPEG or PNG. */
function usePhotoDownload(item: ItemDetail, onError: (message: string | null) => void) {
  const [busy, setBusy] = useState<string | null>(null);
  const pub = item.photos.filter((p) => !p.isPrivate);
  const priv = item.photos.filter((p) => p.isPrivate);
  // Public photos are numbered cover first; private ones apart, so the two never mix in a folder.
  const nameOf = (p: ItemPhoto, format: PhotoFormat) => p.isPrivate
    ? photoFileName(`${item.sku}-private`, priv.indexOf(p) + 1, format)
    : photoFileName(item.sku, pub.indexOf(p) + 1, format);

  async function go(key: string, fn: () => Promise<void>) {
    setBusy(key);
    onError(null);
    try {
      await fn();
    } catch (err) {
      onError(t("item.gallery.downloadFailed", { message: err instanceof Error ? err.message : String(err) }));
    } finally {
      setBusy(null);
    }
  }

  const one = (photo: ItemPhoto, format: PhotoFormat) =>
    go(`one:${format}`, async () => saveBlob(await convertPhoto(photo.url, format), nameOf(photo, format)));

  const all = (format: PhotoFormat) => go(`all:${format}`, async () => {
    const photos = [...pub, ...priv];
    if (photos.length === 1) return saveBlob(await convertPhoto(photos[0]!.url, format), nameOf(photos[0]!, format));
    const files = [];
    for (const p of photos) files.push({ name: nameOf(p, format), blob: await convertPhoto(p.url, format) });
    saveBlob(await zipFiles(files), `${item.sku}-photos-${format === "jpeg" ? "jpg" : "png"}.zip`);
  });

  return { busy, one, all };
}

function Strip({ photos, current, onPick, add }: { photos: ItemPhoto[]; current: ItemPhoto | undefined; onPick: (p: ItemPhoto) => void; add?: React.ReactNode }) {
  return (
    <div className="gallery-strip">
      {photos.map((p) => (
        <button key={p.id} aria-current={p.id === current?.id} onClick={() => onPick(p)} aria-label={tk("slot", p.slot ?? "extra")}>
          <img src={p.thumbUrl} alt="" loading="lazy" />
        </button>
      ))}
      {add}
    </div>
  );
}

/**
 * Public photos go to marketplaces, cover first; private ones (a thrift-store
 * price tag, say) stay with the seller and can never be the cover. Any photo
 * can move between the two.
 */
function Gallery({ item }: { item: ItemDetail }) {
  const ws = useWs();
  const qc = useQueryClient();
  const canWrite = useCan("photos.write");
  const [picked, setPicked] = useState<string | null>(null);
  const [uploading, setUploading] = useState<"public" | "private" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const download = usePhotoDownload(item, setError);
  const [viewing, setViewing] = useState<number | null>(null);
  // The API orders the cover first, then by sort order.
  const pub = item.photos.filter((p) => !p.isPrivate);
  const priv = item.photos.filter((p) => p.isPrivate);
  const current = item.photos.find((p) => p.id === picked) ?? pub[0] ?? priv[0];
  const group = current?.isPrivate ? priv : pub;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: keys.item(ws.id, item.id) });
    void qc.invalidateQueries({ queryKey: keys.items(ws.id) });
    void qc.invalidateQueries({ queryKey: keys.crosslist(ws.id, item.id) });
  };

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function add(files: FileList, isPrivate: boolean) {
    setUploading(isPrivate ? "private" : "public");
    await run(async () => {
      for (const file of Array.from(files)) await uploadPhoto(ws.id, item.id, "extra", await preparePhoto(file), isPrivate);
    });
    setUploading(null);
  }

  const patch = (photo: ItemPhoto, body: { isPrimary?: true; isPrivate?: boolean }) =>
    run(() => request(`/items/${item.id}/photos/${photo.id}`, { method: "PATCH", workspace: ws.id, body }));

  const remove = (photo: ItemPhoto) => {
    if (!confirm(t("item.removePhotoConfirm"))) return;
    setPicked(null);
    void run(() => request(`/items/${item.id}/photos/${photo.id}`, { method: "DELETE", workspace: ws.id }));
  };

  return (
    <div className="gallery">
      <div className="gallery-main">
        {current ? (
          <button className="gallery-open" onClick={() => setViewing([...pub, ...priv].indexOf(current))} aria-label={t("item.carousel.open")}>
            <img src={current.url} alt="" />
          </button>
        ) : <div className="thumb-empty"><IconBox size={40} /></div>}
        {current?.isPrimary && <span className="pill pill-plain ip-photo-badge">{t("item.cover")}</span>}
        {current?.isPrivate && <span className="pill pill-plain ip-photo-badge is-private"><IconLock size={12} />{t("item.gallery.privateBadge")}</span>}
        {current && group.length > 1 && <span className="ip-photo-count">{t("item.photoOf", { index: group.indexOf(current) + 1, total: group.length })}</span>}
        {current && (
          <div className="gallery-actions gallery-actions-dl">
            <span className="gallery-download" role="group" aria-label={t("item.gallery.downloadThis")}>
              <IconDownload size={16} />
              {(["jpeg", "png"] as const).map((f) => (
                <button key={f} className="btn btn-sm" disabled={download.busy !== null} title={t("item.gallery.downloadThisAs", { format: f === "jpeg" ? "JPG" : "PNG" })}
                  onClick={() => void download.one(current, f)}>
                  {download.busy === `one:${f}` ? <span className="spinner spinner-sm" /> : f === "jpeg" ? "JPG" : "PNG"}
                </button>
              ))}
            </span>
          </div>
        )}
        {current && canWrite && (
          <div className="gallery-actions">
            {!current.isPrimary && !current.isPrivate && (
              <button className="btn btn-sm" onClick={() => void patch(current, { isPrimary: true })}><IconStar size={16} />{t("item.makePrimary")}</button>
            )}
            <button className="btn btn-sm" onClick={() => void patch(current, { isPrivate: !current.isPrivate })}>
              {current.isPrivate ? <IconUnlock size={16} /> : <IconLock size={16} />}
              {current.isPrivate ? t("item.gallery.makePublic") : t("item.gallery.makePrivate")}
            </button>
            <button className="btn btn-sm btn-danger" aria-label={t("item.removePhoto")} onClick={() => remove(current)}><IconTrash size={16} /></button>
          </div>
        )}
      </div>

      <section className="ip-gallery-section" aria-label={t("item.gallery.public")}>
        <div className="ip-gallery-head">
          <span className="ip-label">{t("item.gallery.public")} · {pub.length}</span>
          <span className="muted small">{pub.length > 0 ? t("item.gallery.publicHint") : t("item.gallery.noPublic")}</span>
        </div>
        <Strip photos={pub} current={current} onPick={(p) => setPicked(p.id)}
          add={canWrite && <AddTile label={t("item.gallery.addPublic")} busy={uploading === "public"} onFiles={(f) => void add(f, false)} />} />
      </section>

      {(priv.length > 0 || canWrite) && (
        <section className="ip-gallery-section is-private" aria-label={t("item.gallery.private")}>
          <div className="ip-gallery-head">
            <span className="ip-label"><IconLock size={12} /> {t("item.gallery.private")} · {priv.length}</span>
            <span className="muted small">{t("item.gallery.privateHint")}</span>
          </div>
          <Strip photos={priv} current={current} onPick={(p) => setPicked(p.id)}
            add={canWrite && <AddTile label={t("item.gallery.addPrivate")} busy={uploading === "private"} onFiles={(f) => void add(f, true)} />} />
        </section>
      )}
      {item.photos.length > 0 && (
        <div className="gallery-download-all">
          <span className="muted small">{t("item.gallery.downloadAll", { count: item.photos.length })}</span>
          {(["jpeg", "png"] as const).map((f) => (
            <button key={f} className="btn btn-sm btn-ghost" disabled={download.busy !== null} onClick={() => void download.all(f)}>
              {download.busy === `all:${f}` ? <span className="spinner spinner-sm" /> : <IconDownload size={14} />}
              {f === "jpeg" ? "JPG" : "PNG"}
            </button>
          ))}
        </div>
      )}
      {error && <p className="form-error">{error}</p>}
      {viewing !== null && (
        <PhotoCarousel photos={[...pub, ...priv]} start={viewing} onClose={() => setViewing(null)}
          onDownload={(p, f) => void download.one(item.photos.find((x) => x.id === p.id)!, f)}
          busy={download.busy === "one:jpeg" ? "jpeg" : download.busy === "one:png" ? "png" : null} />
      )}
    </div>
  );
}

// ---- AI cataloguing ------------------------------------------------------------------------

/**
 * Where the AI stands on this item: reading the photos, failed, done (which
 * fields it holds and what it says to check), or never run. It fills only
 * empty fields and its own earlier answers, never what a person typed.
 */
function AiBanner({ item }: { item: ItemDetail }) {
  const ws = useWs();
  const qc = useQueryClient();
  const canWrite = useCan("items.write");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await requestEnrich(ws.id, item.id);
      await qc.invalidateQueries({ queryKey: keys.item(ws.id, item.id) });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const again = canWrite && item.photos.length > 0 && (
    <button className="btn btn-sm" disabled={busy} onClick={() => void run()}>
      {busy ? <span className="spinner spinner-sm" /> : <IconSparkle size={14} />}
      {item.ai || item.enrichmentStatus === "failed" ? t("item.ai.again") : t("item.ai.run")}
    </button>
  );

  if (item.enrichmentStatus === "running") {
    return <div className="banner ai-banner"><span className="spinner spinner-sm" /><span className="grow">{t("item.ai.running")}</span></div>;
  }
  if (item.enrichmentStatus === "failed") {
    return (
      <div className="banner banner-error ai-banner">
        <span className="grow">{t("item.ai.failed", { error: item.enrichmentError ?? "" })}</span>
        {again}
        {error && <span className="small">{error}</span>}
      </div>
    );
  }
  if (item.ai) {
    return (
      <div className="ai-note">
        <IconSparkle size={14} />
        <span className="grow small muted">
          {t("item.ai.done", { when: shortDate(item.ai.at), count: item.ai.fields.length })}
          {item.ai.notes && <> · <strong>{t("item.ai.check")}</strong> {item.ai.notes}</>}
        </span>
        {again}
        {error && <span className="small form-error">{error}</span>}
      </div>
    );
  }
  if (!again) return null;
  return (
    <div className="ai-note">
      <IconSparkle size={14} />
      <span className="grow small muted">{t("item.ai.offer")}</span>
      {again}
      {error && <span className="small form-error">{error}</span>}
    </div>
  );
}

// ---- Storage & shipping ---------------------------------------------------------------------

function StorageCard({ item, onEditDetails, onMeasure, onPackaging }: {
  item: ItemDetail;
  onEditDetails: () => void;
  onMeasure: () => void;
  onPackaging: () => void;
}) {
  const ws = useWs();
  const canEdit = useCan("items.write");
  const facts: { label: string; value: string | null; mono?: boolean; fix: () => void }[] = [
    { label: t("intake.bin"), value: item.storageBin, mono: true, fix: onEditDetails },
    { label: t("intake.itemWeight"), value: formatWeightParts(item.itemWeightGrams, ws.units), fix: onPackaging },
    { label: t("item.packagingSummary"), value: packagingSummary(item), fix: onPackaging },
    { label: t("item.measurements"), value: measurementSummary(item.measurements), fix: onMeasure },
  ];
  return (
    <section className="card stack" aria-labelledby="ip-storage">
      <h2 id="ip-storage" className="ip-h3">{t("item.storage")}</h2>
      <dl className="ip-facts">
        {facts.map((f) => [
          <dt key={`${f.label}t`}>{f.label}</dt>,
          <dd key={`${f.label}d`} className={f.mono && f.value ? "mono ip-strong" : ""}>
            {f.value ?? <span className="pill pill-plain ip-pill-warn">{t("item.missing")}</span>}
          </dd>,
          <dd key={`${f.label}a`} className="ip-fact-action">
            {canEdit && (
              <button className="btn btn-sm btn-ghost" onClick={f.fix} aria-label={`${f.value ? t("app.edit") : t("item.add")} · ${f.label}`}>
                {f.value ? t("app.edit") : t("item.add")}
              </button>
            )}
          </dd>,
        ])}
      </dl>
    </section>
  );
}

// ---- Listing details ------------------------------------------------------------------------

type Form = Required<Pick<ItemUpdate,
  "title" | "brand" | "category" | "subcategory" | "sizeLabel" | "colorway" | "conditionGrade" | "conditionNotes" |
  "description" | "audience" | "season" | "purchasePriceMinor" | "tagPriceMinor" | "purchaseDate" | "storageBin" |
  "sourceName" | "isPersonal">> & { materials: string };

function toForm(item: ItemDetail): Form {
  return {
    title: item.title, brand: item.brand, category: item.category, subcategory: item.subcategory,
    sizeLabel: item.sizeLabel, colorway: item.colorway, conditionGrade: item.conditionGrade,
    conditionNotes: item.conditionNotes, description: item.description, audience: item.audience, season: item.season,
    purchasePriceMinor: item.purchasePriceMinor, tagPriceMinor: item.tagPriceMinor, purchaseDate: item.purchaseDate,
    storageBin: item.storageBin, sourceName: item.sourceName, isPersonal: item.isPersonal,
    materials: item.materials.join(", "),
  };
}

function Description({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 240 || text.split("\n").length > 3;
  return (
    <div className="stack-sm">
      <p className={`ip-desc${long && !open ? " ip-clamp" : ""}`}>{text}</p>
      {long && (
        <button className="btn btn-sm btn-ghost ip-link-btn" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t("item.showLess") : t("item.showMore")}
        </button>
      )}
    </div>
  );
}

function Attrs({ rows }: { rows: [string, string | null][] }) {
  const shown = rows.filter(([, v]) => v);
  if (shown.length === 0) return null;
  return (
    <dl className="ip-attrs">
      {shown.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
    </dl>
  );
}

function DetailsPanel({ item, editing, setEditing }: { item: ItemDetail; editing: boolean; setEditing: (on: boolean) => void }) {
  const ws = useWs();
  const canWrite = useCan("items.write");
  const box = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<Form>(() => toForm(item));
  const save = useItemMutation(ws.id, item.id, (body: ItemUpdate) =>
    request<ItemDetail>(`/items/${item.id}`, { method: "PATCH", workspace: ws.id, body }));
  useEffect(() => { if (!editing) setForm(toForm(item)); }, [item, editing]);
  useEffect(() => { if (editing) box.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [editing]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const text = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(k, (e.target.value || null) as never);

  if (!editing) {
    const c = item.currency;
    return (
      <div ref={box}>
        <section className="card stack" aria-labelledby="ip-details">
          <div className="ip-card-head">
            <div>
              <h2 id="ip-details">{t("item.listingDetails")}</h2>
              <p className="muted small">{t("item.listingDetailsHint")}</p>
            </div>
            {canWrite && <button className="btn btn-sm" onClick={() => setEditing(true)}><IconPencil size={14} />{t("app.edit")}</button>}
          </div>
          {item.description && <Description text={item.description} />}
          <Attrs rows={[
            [t("intake.brand"), item.brand],
            [t("intake.category"), [item.category, item.subcategory].filter(Boolean).join(" › ") || null],
            [t("intake.size"), item.sizeLabel],
            [t("intake.condition"), item.conditionGrade ? tk("condition", item.conditionGrade) : null],
            [t("item.colorway"), item.colorway],
            [t("item.materials"), item.materials.join(", ") || null],
            [t("item.audience"), item.audience ? tk("audience", item.audience) : null],
            [t("item.season"), item.season ? tk("season", item.season) : null],
          ]} />
          {item.conditionNotes && (
            <div className="ip-note">
              <span className="ip-label">{t("item.conditionNotes")}</span>
              <span>{item.conditionNotes}</span>
            </div>
          )}
          {(item.purchasePriceMinor !== null || item.tagPriceMinor !== null || item.purchaseDate || item.sourceName) && (
            <div className="stack-sm">
              <h3 className="ip-label">{t("item.purchase")}</h3>
              <Attrs rows={[
                [t("intake.purchasePrice"), item.purchasePriceMinor !== null ? money(item.purchasePriceMinor, c) : null],
                [t("intake.tagPrice"), item.tagPriceMinor !== null ? money(item.tagPriceMinor, c) : null],
                [t("item.purchaseDate"), item.purchaseDate ? shortDate(item.purchaseDate) : null],
                [t("intake.source"), item.sourceName],
              ]} />
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div ref={box}>
      <form className="card stack-lg" onSubmit={(e) => {
        e.preventDefault();
        const { materials, ...rest } = form;
        save.mutate({ ...rest, materials: materials.split(",").map((s) => s.trim()).filter(Boolean) }, { onSuccess: () => setEditing(false) });
      }}>
        <h2>{t("item.editDetails")}</h2>

        <fieldset className="ip-fieldset">
          <legend>{t("item.section.listing")}</legend>
          <Field label={t("intake.titleField")}><input className="input" value={form.title ?? ""} maxLength={280} onChange={text("title")} /></Field>
          <div className="grid-2">
            <Field label={t("intake.brand")}><input className="input" value={form.brand ?? ""} maxLength={120} onChange={text("brand")} /></Field>
            <Field label={t("intake.size")}><input className="input" value={form.sizeLabel ?? ""} maxLength={40} onChange={text("sizeLabel")} /></Field>
            <Field label={t("intake.category")}><input className="input" value={form.category ?? ""} maxLength={80} onChange={text("category")} /></Field>
            <Field label={t("item.subcategory")}><input className="input" value={form.subcategory ?? ""} maxLength={80} onChange={text("subcategory")} /></Field>
            <Field label={t("intake.condition")}>
              <select className="select" value={form.conditionGrade ?? ""} onChange={(e) => set("conditionGrade", (e.target.value || null) as Form["conditionGrade"])}>
                <option value="">—</option>
                {CONDITION_GRADES.map((g) => <option key={g} value={g}>{tk("condition", g)}</option>)}
              </select>
            </Field>
            <Field label={t("item.colorway")}><input className="input" value={form.colorway ?? ""} maxLength={80} onChange={text("colorway")} /></Field>
            <Field label={t("item.audience")}>
              <select className="select" value={form.audience ?? ""} onChange={(e) => set("audience", (e.target.value || null) as Form["audience"])}>
                <option value="">—</option>
                {AUDIENCES.map((a) => <option key={a} value={a}>{tk("audience", a)}</option>)}
              </select>
            </Field>
            <Field label={t("item.season")}>
              <select className="select" value={form.season ?? ""} onChange={(e) => set("season", (e.target.value || null) as Form["season"])}>
                <option value="">—</option>
                {SEASONS.map((s) => <option key={s} value={s}>{tk("season", s)}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("item.materials")} hint={t("item.materialsHint")}>
            <input className="input" value={form.materials} onChange={(e) => set("materials", e.target.value)} />
          </Field>
          <Field label={t("item.description")}>
            <textarea className="textarea" value={form.description ?? ""} maxLength={5000} onChange={text("description")} />
          </Field>
          <Field label={t("item.conditionNotes")}>
            <textarea className="textarea" value={form.conditionNotes ?? ""} maxLength={2000} onChange={text("conditionNotes")} />
          </Field>
        </fieldset>

        <fieldset className="ip-fieldset">
          <legend>{t("item.section.purchase")}</legend>
          <div className="grid-2">
            <Field label={t("intake.purchasePrice")}>
              <MoneyInput currency={item.currency} locale={ws.locale} value={form.purchasePriceMinor} onChange={(v) => set("purchasePriceMinor", v)} />
            </Field>
            <Field label={t("intake.tagPrice")}>
              <MoneyInput currency={item.currency} locale={ws.locale} value={form.tagPriceMinor} onChange={(v) => set("tagPriceMinor", v)} />
            </Field>
            <Field label={t("item.purchaseDate")}>
              <input className="input" type="date" value={form.purchaseDate ?? ""} onChange={(e) => set("purchaseDate", e.target.value || null)} />
            </Field>
            <Field label={t("intake.source")}><input className="input" value={form.sourceName ?? ""} maxLength={120} onChange={text("sourceName")} /></Field>
          </div>
        </fieldset>

        <fieldset className="ip-fieldset">
          <legend>{t("item.section.storage")}</legend>
          <div className="grid-2">
            <Field label={t("intake.bin")}><input className="input" value={form.storageBin ?? ""} maxLength={40} onChange={text("storageBin")} /></Field>
          </div>
          {ws.personalItems && (
            <label className="check">
              <input type="checkbox" checked={form.isPersonal} onChange={(e) => set("isPersonal", e.target.checked)} />
              {t("intake.personal")}
            </label>
          )}
        </fieldset>

        {save.isError && <p className="form-error">{save.error.message}</p>}
        <div className="row">
          <button className="btn btn-primary" disabled={save.isPending}>{save.isPending ? t("app.saving") : t("app.save")}</button>
          <button type="button" className="btn" onClick={() => setEditing(false)}>{t("app.cancel")}</button>
        </div>
      </form>
    </div>
  );
}

// ---- Header actions ----------------------------------------------------------------------

interface MenuItem { label: string; onSelect: () => void; danger?: boolean; disabled?: boolean }

function MoreMenu({ items }: { items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const entries = () => Array.from(wrap.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not(:disabled)") ?? []);

  useEffect(() => {
    if (!open) return;
    entries()[0]?.focus();
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); toggle.current?.focus(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const list = entries();
      const i = list.indexOf(document.activeElement as HTMLButtonElement);
      list[(i + (e.key === "ArrowDown" ? 1 : list.length - 1)) % list.length]?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;
  return (
    <div className="ip-menu-wrap" ref={wrap}>
      <button ref={toggle} className="btn btn-sm ip-icon-only" aria-label={t("item.more")} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <IconMore size={18} />
      </button>
      {open && (
        <div className="ip-menu" role="menu">
          {items.map((m) => (
            <button key={m.label} role="menuitem" className={`ip-menu-item${m.danger ? " ip-danger-text" : ""}`} disabled={m.disabled}
              onClick={() => { setOpen(false); m.onSelect(); }}>
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemActions({ item, onEditDetails, onMeasure, onPackaging, onLabel }: {
  item: ItemDetail;
  onEditDetails: () => void;
  onMeasure: () => void;
  onPackaging: () => void;
  onLabel: () => void;
}) {
  const ws = useWs();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canWrite = useCan("items.write");
  const canDelete = useCan("items.delete");
  const canPrint = useCan("labels.print");
  const canSell = useCan("sales.write");
  const [writeOff, setWriteOff] = useState(false);
  const [selling, setSelling] = useState(false);
  const [reason, setReason] = useState<WriteOff["reason"]>("damaged");
  const patch = useItemMutation(ws.id, item.id, (body: ItemUpdate) =>
    request<ItemDetail>(`/items/${item.id}`, { method: "PATCH", workspace: ws.id, body }));
  const off = useItemMutation(ws.id, item.id, (body: WriteOff) =>
    request<ItemDetail>(`/items/${item.id}/write-off`, { method: "POST", workspace: ws.id, body }));
  const restock = useItemMutation(ws.id, item.id, () =>
    request<ItemDetail>(`/items/${item.id}/restock`, { method: "POST", workspace: ws.id }));
  const ship = useItemMutation(ws.id, item.id, (body: SaleUpdate) =>
    request<ItemDetail>(`/sales/${item.sale!.id}`, { method: "PATCH", workspace: ws.id, body }));

  const sale = activeSale(item);
  const inStock = IN_STOCK.includes(item.status);

  async function del() {
    if (!confirm(t("item.deleteConfirm"))) return;
    await request(`/items/${item.id}`, { method: "DELETE", workspace: ws.id });
    qc.removeQueries({ queryKey: keys.item(ws.id, item.id) });
    void qc.invalidateQueries({ queryKey: keys.items(ws.id) });
    navigate(wsPath(ws, "/inventory"), { replace: true });
  }

  // One main action, chosen by where the item is in its life.
  let primary: { label: string; run: () => void; pending: boolean } | null = null;
  if (canSell && sale && !sale.shippedAt) {
    primary = { label: t("item.markShipped"), run: () => ship.mutate({ shipped: true }), pending: ship.isPending };
  } else if (canSell && !sale && inStock) {
    primary = { label: t("item.recordSale"), run: () => setSelling(true), pending: false };
  }

  const menu: MenuItem[] = [];
  if (canWrite) {
    menu.push({ label: t("item.editDetails"), onSelect: onEditDetails });
    menu.push({ label: t("item.measurements"), onSelect: onMeasure });
    menu.push({ label: t("item.packagingDetails"), onSelect: onPackaging });
    if (inStock) menu.push({ label: `${t("item.writeOff")}…`, onSelect: () => setWriteOff(true) });
    if (["donated", "lost", "damaged"].includes(item.status)) {
      menu.push({ label: t("item.restock"), onSelect: () => restock.mutate(undefined), disabled: restock.isPending });
    }
    menu.push({ label: item.archivedAt ? t("item.unarchive") : t("item.archive"), onSelect: () => patch.mutate({ archived: !item.archivedAt }), disabled: patch.isPending });
    if (canDelete) menu.push({ label: `${t("app.delete")}…`, onSelect: () => void del(), danger: true });
  }

  const err = patch.error ?? off.error ?? restock.error ?? ship.error;

  return (
    <>
      <div className="ip-actions">
        {canPrint && <button className="btn btn-sm" onClick={onLabel}><IconPrinter size={16} />{t("item.printLabel")}</button>}
        <MoreMenu items={menu} />
        {primary && <button className="btn btn-sm btn-accent ip-primary" disabled={primary.pending} onClick={primary.run}>{primary.label}</button>}
      </div>
      {err && <p className="form-error ip-full">{err.message}</p>}

      {primary && (
        <div className="ip-bottom-bar">
          <button className="btn btn-accent" disabled={primary.pending} onClick={primary.run}>{primary.label}</button>
        </div>
      )}

      {selling && <SaleDialog item={item} onClose={() => setSelling(false)} />}
      {writeOff && (
        <Dialog
          title={t("item.writeOff")}
          onClose={() => setWriteOff(false)}
          footer={<>
            <button className="btn" onClick={() => setWriteOff(false)}>{t("app.cancel")}</button>
            <button className="btn btn-danger" disabled={off.isPending} onClick={() => off.mutate({ reason }, { onSuccess: () => setWriteOff(false) })}>{t("item.writeOff")}</button>
          </>}
        >
          <Field label={t("item.writeOffReason")}>
            <select className="select" value={reason} onChange={(e) => setReason(e.target.value as WriteOff["reason"])}>
              {WRITE_OFF_REASONS.map((r) => <option key={r} value={r}>{tk("writeOff", r)}</option>)}
            </select>
          </Field>
        </Dialog>
      )}
    </>
  );
}

// ---- Page ---------------------------------------------------------------------------------

/**
 * Two columns: photos, money and storage beside the work (shipping, marketplaces,
 * listing details). On a phone the cards fall into one column in the order
 * they're needed (the ip-o-* classes).
 */
export function ItemPage() {
  const ws = useWs();
  const { id = "" } = useParams();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const item = useItem(ws.id, id);
  const mpName = useMarketplaceName();
  const [label, setLabel] = useState(false);
  const [measure, setMeasure] = useState(false);
  const [packaging, setPackaging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewTab, setPreviewTab] = useState<string | null>(null);
  const showPreview = (code: string) => {
    setPreviewTab(code);
    requestAnimationFrame(() => document.getElementById("posting-preview")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  if (item.isPending) return <Loading />;
  if (item.isError) return <ErrorBlock error={item.error} onRetry={() => void item.refetch()} />;
  const it = item.data;
  const sale = activeSale(it);
  const meta = [it.brand, it.sizeLabel, it.conditionGrade ? tk("condition", it.conditionGrade) : null, it.colorway].filter(Boolean) as string[];

  return (
    <div className="ip stack-lg">
      <div className="row">
        <Link className="btn btn-ghost btn-sm" to={wsPath(ws, "/inventory")}><IconBack size={18} />{t("nav.inventory")}</Link>
      </div>
      {notice && <div className="banner">{notice}</div>}
      <AiBanner item={it} />

      <div className="ip-layout">
        <header className="ip-head ip-o-head">
          <div className="ip-head-text">
            <div className="row-wrap">
              <StatusPill status={it.status} />
              {it.archivedAt && <span className="pill pill-plain">{t("inventory.archived")}</span>}
              {it.isPersonal && <span className="pill pill-plain">{t("inventory.personal")}</span>}
              {!sale && IN_STOCK.includes(it.status) && it.ledger.daysHeld !== null && (
                <span className="pill pill-plain">{t("item.daysHeld", { days: it.ledger.daysHeld })}</span>
              )}
              <span className="muted small mono">{t("item.sku", { sku: it.sku })}</span>
            </div>
            <h1>{it.title || t("inventory.untitled")}</h1>
            {sale ? (
              <p className="muted">
                {t("item.soldSummary", { marketplace: mpName(sale.marketplace), price: money(sale.salePriceMinor, sale.currency), date: shortDate(sale.soldAt) })}
              </p>
            ) : (meta.length > 0 || it.storageBin) && (
              <p className="muted">
                {meta.join(" · ")}
                {it.storageBin && <>{meta.length > 0 ? " · " : ""}{t("intake.bin")} <strong className="mono ip-strong">{it.storageBin}</strong></>}
              </p>
            )}
          </div>
          <ItemActions
            item={it}
            onEditDetails={() => setEditing(true)}
            onMeasure={() => setMeasure(true)}
            onPackaging={() => setPackaging(true)}
            onLabel={() => setLabel(true)}
          />
        </header>

        <div className="ip-col ip-side">
          <div className="ip-o-gallery"><Gallery item={it} /></div>
          <div className="ip-o-money"><MoneyPanel item={it} /></div>
          <div className="ip-o-storage">
            <StorageCard item={it} onEditDetails={() => setEditing(true)} onMeasure={() => setMeasure(true)} onPackaging={() => setPackaging(true)} />
          </div>
        </div>

        <div className="ip-col ip-main">
          {sale && (
            <div className="ip-o-ship stack">
              <TakedownBanner item={it} />
              <ShipPanel item={it} />
            </div>
          )}
          <div className="ip-o-market"><MarketplacesPanel item={it} onPreview={showPreview} /></div>
          <div className="ip-o-details"><DetailsPanel item={it} editing={editing} setEditing={setEditing} /></div>
          <div className="ip-o-preview"><PostingPreview item={it} tab={previewTab} onTab={setPreviewTab} /></div>
        </div>
      </div>

      {label && <LabelDialog item={it} onClose={() => setLabel(false)} />}
      {measure && <MeasurementsDialog item={it} onClose={() => setMeasure(false)} />}
      {packaging && <PackagingDialog item={it} onClose={() => setPackaging(false)} />}
    </div>
  );
}
